﻿import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    Placement,
    shift,
    useDismiss,
    useFloating,
    useInteractions,
    useTransitionStyles
} from '@floating-ui/react';
import React from 'react';
import { useMultiWindowContext } from '../multi-window/context/multi-window-context';

interface CustomPopoverProps {
    title?: string;
    trigger: React.ReactNode;
    children?: React.ReactNode;
    open?: boolean;
    animateTranslate?: boolean;
    onOpenChange?: (open: boolean) => void;
    offsetOptions?: { crossAxis?: number; mainAxis?: number };
    className?: string;
    contentClassName?: string;
    placement?: Placement;
    closeOnClick?: boolean; //FIXME would be nice if we could specify what elements close the popover
    disableDismiss?: boolean;
    zIndex?: number;
}

export default function CustomPopover({
    title,
    trigger,
    children,
    open: controlledOpen,
    onOpenChange,
    offsetOptions = { crossAxis: -1, mainAxis: 5 },
    className = '',
    animateTranslate = true,
    closeOnClick = true,
    placement,
    contentClassName = 'k-card p-0 overflow-auto z-2',
    disableDismiss = false,
    zIndex = 3
}: CustomPopoverProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);

    const multiWindowContext = useMultiWindowContext();
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

    const setIsOpen = (value: boolean) => {
        if (!isControlled) {
            setUncontrolledOpen(value);
        }
        onOpenChange?.(value);
    };

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        placement: placement,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [shift(), flip(), offset(offsetOptions)]
    });

    const dismiss = useDismiss(context, { enabled: !disableDismiss });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: 100,
        initial: {
            opacity: 0,
            transform: animateTranslate ? 'translateY(-50px)' : undefined
        }
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

    const handleTriggerClick = (e: React.MouseEvent) => {
        setIsOpen(!isOpen);

        // If trigger has its own onClick, call it
        if (React.isValidElement(trigger) && (trigger.props as any).onClick) {
            (trigger.props as any).onClick(e);
        }
    };

    // Clone trigger element to add ref and onClick
    const triggerElement = React.isValidElement(trigger)
        ? React.cloneElement(trigger as React.ReactElement<any>, {
              onClick: handleTriggerClick
          })
        : trigger;

    return (
        <>
            <div title={title} data-tooltip-bottom ref={refs.setReference} {...getReferenceProps()}>
                {triggerElement}
            </div>

            <FloatingPortal root={multiWindowContext.root}>
                {isMounted && (
                    <div
                        className={`p-0 overflow-auto ${className}`}
                        ref={refs.setFloating}
                        {...getFloatingProps()}
                        onClick={() => closeOnClick && setIsOpen(false)}
                        style={{ ...floatingStyles, zIndex }}>
                        <div className={contentClassName} style={transitionStyles}>
                            {children}
                        </div>
                    </div>
                )}
            </FloatingPortal>
        </>
    );
}
